import React from 'react';
import { APP_CONTAINER_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams } from '@/navigators/types';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { BatchTagPrintingData } from '@/adapters/offline-mode';
import { SelectTagPrintBatchTemplate } from './select-tag-print-batch-template';
import { StackNavigationProp } from '@react-navigation/stack';
import { useNavigation } from '@react-navigation/native';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useSaveTagPrintBatch } from '@/hooks/use-save-tag-print-batch';
import { useToasts } from '@/contexts/toast-context';
import { v4 as uuidv4 } from 'uuid';

type Navigation = StackNavigationProp<
  AppContainerStackParams,
  APP_CONTAINER_ROUTES.TagPrintBatch
>;

interface Props {
  offlineTagBatchPrintData: BatchTagPrintingData | null;
  onClose: () => void;
  updateTagPrintBatchData: (
    tagBatchPrintData: BatchTagPrintingData,
  ) => Promise<void>;
}

export const CreateTagPrintBatchActionSheet: React.FC<Props> = ({
  offlineTagBatchPrintData,
  onClose,
  updateTagPrintBatchData,
}) => {
  const { navigate } = useNavigation<Navigation>();
  const { showErrorToast } = useToasts();
  const { createTagPrintBatch } = useSaveTagPrintBatch();
  const isOfflineModeActive = useOfflineModeActive();
  return (
    <BaseActionSheet isVisible onClose={onClose}>
      <SelectTagPrintBatchTemplate
        initialTemplate={null}
        onSelectTemplate={async (selectedTemplate) => {
          if (selectedTemplate) {
            if (isOfflineModeActive) {
              navigate(APP_CONTAINER_ROUTES.TagPrintBatch, {
                printTagBatchUUID: uuidv4(),
                selectedTemplateUUID: selectedTemplate.uuid,
              });
              onClose();
            } else {
              try {
                const { data } = await createTagPrintBatch(
                  selectedTemplate.uuid,
                );

                if (data?.upsertPrintTagBatchForRestaurant.print_tag_batch) {
                  const newBatch =
                    data.upsertPrintTagBatchForRestaurant.print_tag_batch;
                  navigate(APP_CONTAINER_ROUTES.TagPrintBatch, {
                    printTagBatchUUID: newBatch.uuid,
                  });
                  if (offlineTagBatchPrintData) {
                    await updateTagPrintBatchData({
                      ...offlineTagBatchPrintData,
                      batches: {
                        [newBatch.uuid]: newBatch,
                        ...offlineTagBatchPrintData.batches,
                      },
                    });
                  }
                  onClose();
                }
              } catch {
                showErrorToast();
              }
            }
          }
        }}
      />
    </BaseActionSheet>
  );
};
